import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaReceipt,
  FaRupeeSign,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const PAYMENT_MODES = ['Cash', 'Cheque', 'Online / NEFT / UPI', 'Bank Transfer'];

const TEMPLATES = [
  { id: 'classic', label: 'Classic Blue', accent: '#2563eb' },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827' },
  { id: 'modern', label: 'Modern Green', accent: '#059669' },
  { id: 'formal', label: 'Formal Bordered', accent: '#7c2d12' },
  { id: 'compact', label: 'Compact Slip', accent: '#0f766e' },
];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Indian FY months in order: Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec, Jan, Feb, Mar
const FY_MONTH_ORDER = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2];

const todayISO = () => new Date().toISOString().split('T')[0];

const generateReceiptNumber = () => {
  const year = new Date().getFullYear();
  const seq = Math.floor(1000 + Math.random() * 9000);
  return `RR-${year}-${seq}`;
};

// Number → Indian words (Rupees ... Only)
const numberToWordsIndian = (num) => {
  num = Math.round(Number(num) || 0);
  if (num === 0) return 'Zero';
  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const twoDigits = (n) => {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
  };
  const threeDigits = (n) => {
    if (n < 100) return twoDigits(n);
    const h = Math.floor(n / 100);
    const r = n % 100;
    return ones[h] + ' Hundred' + (r ? ' ' + twoDigits(r) : '');
  };

  let words = '';
  const crore = Math.floor(num / 10000000);
  num %= 10000000;
  const lakh = Math.floor(num / 100000);
  num %= 100000;
  const thousand = Math.floor(num / 1000);
  num %= 1000;
  const hundred = num;

  if (crore) words += threeDigits(crore) + ' Crore ';
  if (lakh) words += twoDigits(lakh) + ' Lakh ';
  if (thousand) words += twoDigits(thousand) + ' Thousand ';
  if (hundred) words += threeDigits(hundred);
  return words.trim();
};

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

// ---------- LIVE PREVIEW COMPONENTS ----------
const ReceiptPreview = ({ template, r, tenant, landlord, propertyAddress, panRequired, logo }) => {
  const accent =
    TEMPLATES.find((t) => t.id === template)?.accent || '#2563eb';

  const Note = () => (
    <>
      {r.paymentMode === 'Cash' && r.amount > 5000 && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
          Note: Affix Re. 1 revenue stamp for cash payments above ₹5,000.
        </div>
      )}
      {panRequired && !landlord.pan && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#dc2626', fontStyle: 'italic' }}>
          Landlord PAN is mandatory because annual rent exceeds ₹1,00,000.
        </div>
      )}
    </>
  );

  const Signature = () => (
    <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            borderTop: '1px solid #94a3b8',
            width: 180,
            paddingTop: 6,
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          Landlord Signature
        </div>
      </div>
    </div>
  );

  // ---- 1. CLASSIC ----
  if (template === 'classic') {
    return (
      <div style={{ padding: 24 }}>
        {logo && (
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <img src={logo} alt="logo" style={{ height: 50, objectFit: 'contain' }} />
          </div>
        )}
        <div
          style={{
            textAlign: 'center',
            fontWeight: 800,
            fontSize: 22,
            color: accent,
            letterSpacing: 1,
          }}
        >
          RENT RECEIPT
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: 13 }}>
          <span>Receipt No: <strong>{r.receiptNo}</strong></span>
          <span>Date: <strong>{formatDate(r.date)}</strong></span>
        </div>
        <div
          style={{
            marginTop: 12,
            background: accent,
            color: '#fff',
            borderRadius: 8,
            padding: '10px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 700,
          }}
        >
          <span>₹ {formatINR(r.amount)}</span>
          <span>for {r.periodLabel}</span>
        </div>
        <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.6 }}>
          Received <strong>₹ {formatINR(r.amount)}</strong>{' '}
          (<em>Rupees {numberToWordsIndian(r.amount)} Only</em>) from{' '}
          <strong>{tenant.name || '—'}</strong>
          {tenant.pan && <> (PAN: <strong>{tenant.pan}</strong>)</>} towards rent for{' '}
          <strong>{propertyAddress || '—'}</strong> for{' '}
          <strong>{r.periodLabel}</strong>, paid via <strong>{r.paymentMode}</strong>
          {r.transactionRef && <> (Ref: <strong>{r.transactionRef}</strong>)</>}.
        </div>
        <div style={{ marginTop: 14, fontSize: 14 }}>
          <div><strong>Landlord:</strong> {landlord.name || '—'}</div>
          {landlord.pan && <div><strong>Landlord PAN:</strong> {landlord.pan}</div>}
          {landlord.address && <div><strong>Address:</strong> {landlord.address}</div>}
        </div>
        <Note />
        <Signature />
      </div>
    );
  }

  // ---- 2. MINIMAL ----
  if (template === 'minimal') {
    return (
      <div style={{ padding: 28, border: '1px solid #e5e7eb', margin: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {logo && <img src={logo} alt="logo" style={{ height: 44, objectFit: 'contain' }} />}
            <div style={{ fontSize: 26, fontWeight: 800, color: '#111827' }}>Rent Receipt</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11, color: '#64748b' }}>
            <div>No. {r.receiptNo}</div>
            <div>{formatDate(r.date)}</div>
          </div>
        </div>
        <div style={{ borderBottom: '2px solid #111827', margin: '12px 0 18px' }} />
        <div style={{ fontSize: 30, fontWeight: 800, color: '#111827' }}>
          ₹ {formatINR(r.amount)}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>for {r.periodLabel}</div>

        <div style={{ marginTop: 18, fontSize: 13, lineHeight: 1.7 }}>
          <div><strong>Tenant:</strong> {tenant.name || '—'}{tenant.pan && ` (PAN ${tenant.pan})`}</div>
          <div><strong>Property:</strong> {propertyAddress || '—'}</div>
          <div><strong>Period:</strong> {r.periodLabel}</div>
          <div><strong>Payment:</strong> {r.paymentMode}{r.transactionRef && ` · ${r.transactionRef}`}</div>
          <div style={{ marginTop: 8 }}><strong>Landlord:</strong> {landlord.name || '—'}</div>
          {landlord.pan && <div><strong>Landlord PAN:</strong> {landlord.pan}</div>}
          {landlord.address && <div><strong>Address:</strong> {landlord.address}</div>}
        </div>
        <Note />
        <Signature />
      </div>
    );
  }

  // ---- 3. MODERN GREEN SIDEBAR ----
  if (template === 'modern') {
    return (
      <div style={{ display: 'flex', minHeight: 420 }}>
        <div
          style={{
            background: accent,
            color: '#fff',
            padding: 18,
            width: 130,
            flexShrink: 0,
          }}
        >
          {logo && (
            <div style={{ marginBottom: 10, background: '#fff', borderRadius: 6, padding: 4, display: 'inline-block' }}>
              <img src={logo} alt="logo" style={{ height: 36, objectFit: 'contain' }} />
            </div>
          )}
          <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>RENT<br />RECEIPT</div>
          <div style={{ marginTop: 20, fontSize: 10, opacity: 0.85 }}>AMOUNT</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>
            ₹ {formatINR(r.amount)}
          </div>
          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 4 }}>for {r.periodLabel}</div>
        </div>
        <div style={{ padding: 20, flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>No: <strong>{r.receiptNo}</strong></span>
            <span><strong>{formatDate(r.date)}</strong></span>
          </div>
          <div style={{ borderBottom: `1px solid ${accent}`, margin: '10px 0 14px' }} />
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div><strong>Tenant:</strong> {tenant.name || '—'}</div>
            {tenant.pan && <div><strong>PAN:</strong> {tenant.pan}</div>}
            <div><strong>Property:</strong> {propertyAddress || '—'}</div>
            <div><strong>Payment:</strong> {r.paymentMode}</div>
            {r.transactionRef && <div><strong>Ref:</strong> {r.transactionRef}</div>}
            <div style={{ marginTop: 8 }}><strong>Landlord:</strong> {landlord.name || '—'}</div>
            {landlord.pan && <div><strong>Landlord PAN:</strong> {landlord.pan}</div>}
            {landlord.address && <div><strong>Address:</strong> {landlord.address}</div>}
          </div>
          <Note />
          <Signature />
        </div>
      </div>
    );
  }

  // ---- 4. FORMAL BORDERED ----
  if (template === 'formal') {
    return (
      <div
        style={{
          padding: 18,
          border: `2px solid ${accent}`,
          margin: 6,
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}
      >
        <div style={{ border: `1px solid ${accent}`, padding: 18 }}>
          {logo && (
            <div style={{ textAlign: 'center', marginBottom: 6 }}>
              <img src={logo} alt="logo" style={{ height: 48, objectFit: 'contain' }} />
            </div>
          )}
          <div style={{ textAlign: 'center', fontSize: 22, fontWeight: 700, color: accent }}>
            RENT RECEIPT
          </div>
          <div style={{ textAlign: 'center', fontStyle: 'italic', color: '#9a3412', fontSize: 12, marginTop: 4 }}>
            (For House Rent Allowance Exemption)
          </div>
          <div style={{ borderBottom: `1px solid ${accent}`, margin: '12px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>Receipt No.: <strong>{r.receiptNo}</strong></span>
            <span>Dated: <strong>{formatDate(r.date)}</strong></span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 14 }}>
            <span style={{ fontWeight: 700 }}>Amount: ₹ {formatINR(r.amount)}</span>
            <span style={{ fontStyle: 'italic' }}>(for {r.periodLabel})</span>
          </div>
          <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.7 }}>
            Received a sum of <strong>₹ {formatINR(r.amount)}</strong>{' '}
            (<em>Rupees {numberToWordsIndian(r.amount)} Only</em>) from{' '}
            <strong>{tenant.name || '—'}</strong>
            {tenant.pan && <> (PAN: <strong>{tenant.pan}</strong>)</>}, towards rent of the property at{' '}
            <strong>{propertyAddress || '—'}</strong> for the period{' '}
            <strong>{r.periodLabel}</strong>, paid via <strong>{r.paymentMode}</strong>
            {r.transactionRef && <> (Ref: <strong>{r.transactionRef}</strong>)</>}.
          </div>
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <div><strong>Landlord:</strong> {landlord.name || '—'}</div>
            {landlord.pan && <div><strong>Landlord PAN:</strong> {landlord.pan}</div>}
            {landlord.address && <div><strong>Address:</strong> {landlord.address}</div>}
          </div>
          <Note />
          <Signature />
        </div>
      </div>
    );
  }

  // ---- 5. COMPACT SLIP ----
  if (template === 'compact') {
    const cell = (label, value) => (
      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: accent, letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 12, color: '#0f172a', marginTop: 2 }}>{value || '—'}</div>
      </div>
    );
    return (
      <div>
        <div
          style={{
            background: accent,
            color: '#fff',
            padding: '10px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {logo && (
              <img src={logo} alt="logo" style={{ height: 28, background: '#fff', borderRadius: 4, padding: 2, objectFit: 'contain' }} />
            )}
            <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: 1 }}>RENT RECEIPT</span>
          </span>
          <span style={{ fontSize: 11 }}>No. {r.receiptNo} · {formatDate(r.date)}</span>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {cell('TENANT', tenant.name)}
            {cell('LANDLORD', landlord.name)}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 }}>
            {cell('PROPERTY', propertyAddress)}
            {cell('LANDLORD PAN', landlord.pan || (panRequired ? 'REQUIRED' : '—'))}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 }}>
            {cell('PERIOD', r.periodLabel)}
            {cell('PAYMENT', r.paymentMode + (r.transactionRef ? ` (${r.transactionRef})` : ''))}
          </div>
          <div
            style={{
              marginTop: 14,
              border: `1.5px solid ${accent}`,
              borderRadius: 6,
              padding: '10px 14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 800, color: accent }}>
              ₹ {formatINR(r.amount)}
            </span>
            <span style={{ fontSize: 10, fontStyle: 'italic', color: '#0f172a', textAlign: 'right', maxWidth: 240 }}>
              Rupees {numberToWordsIndian(r.amount)} Only
            </span>
          </div>
          <Note />
          <Signature />
        </div>
      </div>
    );
  }

  return null;
};

const RentReceiptGenerator = () => {
  // Tenant
  const [tenant, setTenant] = useState({
    name: '',
    pan: '',
  });

  // Landlord
  const [landlord, setLandlord] = useState({
    name: '',
    pan: '',
    address: '',
  });

  // Property
  const [propertyAddress, setPropertyAddress] = useState('');

  // Single receipt details
  const [single, setSingle] = useState({
    receiptNo: generateReceiptNumber(),
    date: todayISO(),
    rentAmount: 15000,
    periodMonth: new Date().getMonth(), // 0-11
    periodYear: new Date().getFullYear(),
    paymentMode: 'Cash',
    transactionRef: '',
  });

  // Template
  const [template, setTemplate] = useState('classic');
  const [logo, setLogo] = useState(null);

  // Bulk mode
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const [bulk, setBulk] = useState({
    startMonth: 3, // April (FY start)
    startYear: new Date().getFullYear(),
    months: 12,
    rentAmount: 15000,
    paymentMode: 'Cash',
  });

  const annualRent = useMemo(() => {
    if (mode === 'bulk') return Number(bulk.rentAmount || 0) * Number(bulk.months || 0);
    return Number(single.rentAmount || 0) * 12;
  }, [mode, single.rentAmount, bulk.rentAmount, bulk.months]);

  const panRequired = annualRent > 100000;

  const buildSingleReceipts = () => {
    if (mode === 'single') {
      return [
        {
          receiptNo: single.receiptNo,
          date: single.date,
          amount: Number(single.rentAmount) || 0,
          periodLabel: `${MONTHS[single.periodMonth]} ${single.periodYear}`,
          paymentMode: single.paymentMode,
          transactionRef: single.transactionRef,
        },
      ];
    }
    // bulk
    const list = [];
    let m = Number(bulk.startMonth);
    let y = Number(bulk.startYear);
    const baseYear = new Date().getFullYear();
    for (let i = 0; i < Number(bulk.months || 0); i++) {
      // First-of-next-month receipt date is typical
      const receiptDate = new Date(y, m, 5);
      const iso = `${receiptDate.getFullYear()}-${String(
        receiptDate.getMonth() + 1
      ).padStart(2, '0')}-05`;
      list.push({
        receiptNo: `RR-${baseYear}-${String(1000 + i).padStart(4, '0')}`,
        date: iso,
        amount: Number(bulk.rentAmount) || 0,
        periodLabel: `${MONTHS[m]} ${y}`,
        paymentMode: bulk.paymentMode,
        transactionRef: '',
      });
      m += 1;
      if (m > 11) {
        m = 0;
        y += 1;
      }
    }
    return list;
  };

  const previewReceipts = buildSingleReceipts();

  // ---------- PDF TEMPLATE RENDERERS ----------

  // Shared "body" used by classic/modern/formal
  const renderBodyBlock = (doc, r, startY, left, right) => {
    let y = startY;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);

    const intro = doc.splitTextToSize(
      `Received a sum of Rs. ${formatINR(r.amount)} (Rupees ${numberToWordsIndian(
        r.amount
      )} Only) from`,
      right - left
    );
    doc.text(intro, left, y);
    y += intro.length * 6 + 2;

    const wrap = (label, value) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, left, y);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(value || '-', right - left - 45);
      doc.text(lines, left + 45, y);
      y += Math.max(6, lines.length * 6);
    };

    wrap('Tenant:', tenant.name);
    if (tenant.pan) wrap('Tenant PAN:', tenant.pan);
    wrap('Towards rent of:', propertyAddress);
    wrap('For Period:', r.periodLabel);
    wrap('Payment Mode:', r.paymentMode);
    if (r.transactionRef) wrap('Transaction Ref:', r.transactionRef);
    y += 4;
    wrap('Landlord:', landlord.name);
    if (landlord.pan) wrap('Landlord PAN:', landlord.pan);
    if (landlord.address) wrap('Landlord Address:', landlord.address);

    if (r.paymentMode === 'Cash' && r.amount > 5000) {
      y += 4;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(
        'Note: Affix Re. 1 revenue stamp for cash payments exceeding Rs. 5,000.',
        left,
        y
      );
      doc.setTextColor(0, 0, 0);
      y += 6;
    }

    // Signature
    y += 14;
    doc.setDrawColor(150, 150, 150);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Landlord Signature', right - 30, y + 6, { align: 'center' });
    y += 10;

    if (panRequired && !landlord.pan) {
      y += 6;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(200, 30, 30);
      doc.text(
        'As annual rent exceeds Rs. 1,00,000, landlord PAN is mandatory for HRA exemption.',
        left,
        y
      );
      doc.setTextColor(0, 0, 0);
    }
    return y;
  };

  // 1. Classic Blue
  const renderClassic = (doc, r, startY) => {
    const pageW = 210;
    const left = 18;
    const right = pageW - 18;
    const accent = hexToRgbArr('#2563eb');
    let y = startY;
    const boxTop = y;

    if (logo) drawPdfLogo(doc, logo, left, y, 16, landlord.name, [240, 240, 240]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...accent);
    doc.text('RENT RECEIPT', pageW / 2, y + 10, { align: 'center' });

    y += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Receipt No: ${r.receiptNo}`, left, y);
    doc.text(`Date: ${formatDate(r.date)}`, right, y, { align: 'right' });

    y += 10;
    doc.setFillColor(...accent);
    doc.rect(left, y, right - left, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(`Rs. ${formatINR(r.amount)}`, left + 4, y + 8);
    doc.text(`for ${r.periodLabel}`, right - 4, y + 8, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    y += 22;
    const endY = renderBodyBlock(doc, r, y, left, right);

    doc.setDrawColor(...accent);
    doc.setLineWidth(0.4);
    doc.rect(left - 4, boxTop - 4, right - left + 8, Math.max(endY, startY + 120) - boxTop + 10);
  };

  // 2. Minimal Mono
  const renderMinimal = (doc, r, startY) => {
    const pageW = 210;
    const left = 22;
    const right = pageW - 22;
    let y = startY;

    if (logo) drawPdfLogo(doc, logo, right - 18, y - 2, 18, landlord.name, [240, 240, 240]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(17, 24, 39);
    doc.text('Rent Receipt', left, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`No. ${r.receiptNo}`, right, y + 4, { align: 'right' });
    doc.text(formatDate(r.date), right, y + 10, { align: 'right' });

    y += 16;
    doc.setDrawColor(17, 24, 39);
    doc.setLineWidth(0.6);
    doc.line(left, y, right, y);

    y += 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(17, 24, 39);
    doc.text(`Rs. ${formatINR(r.amount)}`, left, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`for ${r.periodLabel}`, left, y + 6);

    y += 18;
    renderBodyBlock(doc, r, y, left, right);

    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.rect(left - 6, startY - 4, right - left + 12, 260);
  };

  // 3. Modern Green Sidebar
  const renderModern = (doc, r, startY) => {
    const pageW = 210;
    const accent = hexToRgbArr('#059669');
    const sidebarW = 50;
    const left = sidebarW + 10;
    const right = pageW - 14;
    let y = startY;

    // Sidebar
    doc.setFillColor(...accent);
    doc.rect(0, startY - 4, sidebarW, 280, 'F');
    if (logo) drawPdfLogo(doc, logo, (sidebarW - 24) / 2, startY + 2, 24, 'R', [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('RENT', 8, logo ? startY + 36 : startY + 16);
    doc.text('RECEIPT', 8, logo ? startY + 46 : startY + 26);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('AMOUNT', 8, startY + 44);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    const amtLines = doc.splitTextToSize(`Rs. ${formatINR(r.amount)}`, sidebarW - 12);
    doc.text(amtLines, 8, startY + 52);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`for ${r.periodLabel}`, 8, startY + 52 + amtLines.length * 6 + 2);

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Receipt No: ${r.receiptNo}`, left, y + 6);
    doc.text(`Date: ${formatDate(r.date)}`, right, y + 6, { align: 'right' });

    y += 14;
    doc.setDrawColor(...accent);
    doc.setLineWidth(0.4);
    doc.line(left, y, right, y);
    y += 8;

    renderBodyBlock(doc, r, y, left, right);
  };

  // 4. Formal Bordered (double-border, serif-style)
  const renderFormal = (doc, r, startY) => {
    const pageW = 210;
    const left = 22;
    const right = pageW - 22;
    const accent = hexToRgbArr('#7c2d12');
    let y = startY;

    // Double border
    doc.setDrawColor(...accent);
    doc.setLineWidth(0.8);
    doc.rect(left - 6, startY - 4, right - left + 12, 260);
    doc.setLineWidth(0.3);
    doc.rect(left - 3, startY - 1, right - left + 6, 254);

    if (logo) drawPdfLogo(doc, logo, left, y + 2, 18, landlord.name, [240, 240, 240]);
    doc.setFont('times', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...accent);
    doc.text('RENT RECEIPT', pageW / 2, y + 12, { align: 'center' });

    y += 16;
    doc.setFont('times', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(120, 53, 15);
    doc.text('(For House Rent Allowance Exemption)', pageW / 2, y, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    y += 8;
    doc.setDrawColor(...accent);
    doc.setLineWidth(0.3);
    doc.line(left, y, right, y);

    y += 10;
    doc.setFont('times', 'normal');
    doc.setFontSize(11);
    doc.text(`Receipt No.: ${r.receiptNo}`, left, y);
    doc.text(`Dated: ${formatDate(r.date)}`, right, y, { align: 'right' });

    y += 10;
    doc.setFont('times', 'bold');
    doc.setFontSize(14);
    doc.text(`Amount: Rs. ${formatINR(r.amount)}`, left, y);
    doc.setFont('times', 'italic');
    doc.setFontSize(10);
    doc.text(`(for ${r.periodLabel})`, right, y, { align: 'right' });

    y += 10;
    renderBodyBlock(doc, r, y, left, right);
  };

  // 5. Compact Slip (half-page slip)
  const renderCompact = (doc, r, startY) => {
    const pageW = 210;
    const left = 14;
    const right = pageW - 14;
    const accent = hexToRgbArr('#0f766e');
    let y = startY;

    // Top accent bar
    doc.setFillColor(...accent);
    doc.rect(left - 4, y - 4, right - left + 8, 14, 'F');
    if (logo) drawPdfLogo(doc, logo, left, y - 3, 12, landlord.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('RENT RECEIPT', logo ? left + 16 : left, y + 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`No. ${r.receiptNo}  |  ${formatDate(r.date)}`, right, y + 4, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    y += 16;

    // Two-column compact data
    doc.setFontSize(9);
    const col = (label, value, x, ww) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 118, 110);
      doc.text(label, x, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const lines = doc.splitTextToSize(value || '-', ww);
      doc.text(lines, x, y + 4);
      return lines.length * 4 + 6;
    };
    const halfW = (right - left) / 2 - 4;
    const h1 = col('TENANT', tenant.name, left, halfW);
    const h2 = col('LANDLORD', landlord.name, left + halfW + 8, halfW);
    y += Math.max(h1, h2);

    const h3 = col('PROPERTY', propertyAddress, left, halfW);
    const h4 = col(
      'LANDLORD PAN',
      landlord.pan || (panRequired ? 'REQUIRED' : '—'),
      left + halfW + 8,
      halfW
    );
    y += Math.max(h3, h4);

    const h5 = col('PERIOD', r.periodLabel, left, halfW);
    const h6 = col(
      'PAYMENT',
      r.paymentMode + (r.transactionRef ? ` (${r.transactionRef})` : ''),
      left + halfW + 8,
      halfW
    );
    y += Math.max(h5, h6);

    // Amount box
    y += 4;
    doc.setDrawColor(...accent);
    doc.setLineWidth(0.5);
    doc.rect(left, y, right - left, 18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...accent);
    doc.text(`Rs. ${formatINR(r.amount)}`, left + 4, y + 12);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    const words = doc.splitTextToSize(
      `Rupees ${numberToWordsIndian(r.amount)} Only`,
      right - left - 8
    );
    doc.text(words, right - 4, y + 8, { align: 'right' });

    y += 26;

    if (r.paymentMode === 'Cash' && r.amount > 5000) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text('Affix Re. 1 revenue stamp for cash > Rs. 5,000.', left, y);
      doc.setTextColor(0, 0, 0);
      y += 5;
    }

    // Signature
    y += 8;
    doc.setDrawColor(150, 150, 150);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Landlord Signature', right - 30, y + 5, { align: 'center' });

    if (panRequired && !landlord.pan) {
      y += 12;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(200, 30, 30);
      doc.text(
        'Landlord PAN required: annual rent exceeds Rs. 1,00,000.',
        left,
        y
      );
      doc.setTextColor(0, 0, 0);
    }
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassic,
    minimal: renderMinimal,
    modern: renderModern,
    formal: renderFormal,
    compact: renderCompact,
  };

  const buildPDF = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const receipts = buildSingleReceipts();
    const renderer = TEMPLATE_RENDERERS[template] || renderClassic;
    receipts.forEach((r, idx) => {
      if (idx > 0) doc.addPage();
      renderer(doc, r, 18);
    });
    return doc;
  };

  const handleDownloadPDF = () => {
    const doc = buildPDF();
    const name =
      mode === 'bulk'
        ? `Rent_Receipts_${bulk.months}_months.pdf`
        : `Rent_Receipt_${single.receiptNo}.pdf`;
    doc.save(name);
  };

  const handlePrint = () => {
    const doc = buildPDF();
    doc.autoPrint();
    const url = doc.output('bloburl');
    window.open(url, '_blank');
  };

  const handleReset = () => {
    setTenant({ name: '', pan: '' });
    setLandlord({ name: '', pan: '', address: '' });
    setPropertyAddress('');
    setSingle({
      receiptNo: generateReceiptNumber(),
      date: todayISO(),
      rentAmount: 0,
      periodMonth: new Date().getMonth(),
      periodYear: new Date().getFullYear(),
      paymentMode: 'Cash',
      transactionRef: '',
    });
    setBulk({
      startMonth: 3,
      startYear: new Date().getFullYear(),
      months: 12,
      rentAmount: 0,
      paymentMode: 'Cash',
    });
    setMode('single');
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Rent Receipt Generator"
        description="Free Rent Receipt Generator India for HRA claim. Create monthly or annual rent receipts with landlord PAN, address and signature. Download PDF instantly."
        keywords="rent receipt generator, rent receipt for HRA, HRA rent receipt, landlord PAN rent receipt, monthly rent receipt, rent receipt PDF India, rent receipt format"
        canonicalPath="/rent-receipt"
        calculatorType="RentReceiptGenerator"
        calculatorDescription="Generate rent receipts for HRA exemption in India with landlord PAN, monthly bulk receipts and instant PDF download."
      />

      <div className="calculator-header">
        <h1>Rent Receipt Generator</h1>
        <p>HRA-ready rent receipts with landlord PAN and signature — download as PDF</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Mode</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {[
                { v: 'single', l: 'Single Receipt' },
                { v: 'bulk', l: 'Bulk (Monthly)' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  className={`btn ${mode === opt.v ? 'btn-primary' : 'btn-outline'}`}
                  style={{ padding: '6px 14px' }}
                  onClick={() => setMode(opt.v)}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          <div className="input-group">
            <label>Template</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {TEMPLATES.map((t) => {
                const active = template === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? t.accent : '#cbd5e1'}`,
                      background: active ? t.accent : '#fff',
                      color: active ? '#fff' : '#0f172a',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: t.accent,
                        border: active ? '1px solid #fff' : 'none',
                      }}
                    />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <LogoUploader logo={logo} setLogo={setLogo} label="Landlord / Property Logo" accent="#2563eb" />

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>Tenant Details</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Tenant Name</label>
              <input
                type="text"
                value={tenant.name}
                onChange={(e) => setTenant({ ...tenant, name: e.target.value })}
                placeholder="e.g. Anaya Verma"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Tenant PAN (optional)</label>
              <input
                type="text"
                value={tenant.pan}
                onChange={(e) => setTenant({ ...tenant, pan: e.target.value.toUpperCase() })}
                placeholder="ABCDE1234F"
                maxLength={10}
              />
            </div>
          </div>

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>Landlord Details</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Landlord Name</label>
              <input
                type="text"
                value={landlord.name}
                onChange={(e) => setLandlord({ ...landlord, name: e.target.value })}
                placeholder="e.g. Rajesh Kumar"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>
                Landlord PAN
                {panRequired && (
                  <span style={{ color: '#dc2626', marginLeft: 6, fontSize: 12 }}>
                    (required for HRA)
                  </span>
                )}
              </label>
              <input
                type="text"
                value={landlord.pan}
                onChange={(e) =>
                  setLandlord({ ...landlord, pan: e.target.value.toUpperCase() })
                }
                placeholder="ABCDE1234F"
                maxLength={10}
              />
            </div>
          </div>
          <div className="input-group">
            <label>Landlord Address</label>
            <input
              type="text"
              value={landlord.address}
              onChange={(e) => setLandlord({ ...landlord, address: e.target.value })}
              placeholder="Full address of landlord"
            />
          </div>

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>Rented Property</h3>
          <div className="input-group">
            <label>Property Address</label>
            <input
              type="text"
              value={propertyAddress}
              onChange={(e) => setPropertyAddress(e.target.value)}
              placeholder="e.g. Flat 302, Lakeview Apartments, Hyderabad 500032"
            />
          </div>

          {mode === 'single' ? (
            <>
              <h3 style={{ marginTop: 12, marginBottom: 12 }}>Receipt Details</h3>
              <div className="flex-row">
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Receipt No</label>
                  <input
                    type="text"
                    value={single.receiptNo}
                    onChange={(e) =>
                      setSingle({ ...single, receiptNo: e.target.value })
                    }
                  />
                </div>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Date</label>
                  <input
                    type="date"
                    value={single.date}
                    onChange={(e) => setSingle({ ...single, date: e.target.value })}
                  />
                </div>
              </div>

              <div className="input-group">
                <label>Rent Amount (₹)</label>
                <div className="input-with-icon">
                  <FaRupeeSign className="icon" />
                  <input
                    type="number"
                    value={single.rentAmount === '' ? '' : single.rentAmount}
                    onChange={(e) =>
                      setSingle({
                        ...single,
                        rentAmount:
                          e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                    min="0"
                    step="1"
                  />
                </div>
              </div>

              <div className="flex-row">
                <div className="input-group" style={{ flex: 1 }}>
                  <label>For Month</label>
                  <select
                    value={single.periodMonth}
                    onChange={(e) =>
                      setSingle({ ...single, periodMonth: Number(e.target.value) })
                    }
                  >
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Year</label>
                  <input
                    type="number"
                    value={single.periodYear}
                    onChange={(e) =>
                      setSingle({ ...single, periodYear: Number(e.target.value) })
                    }
                    min="2000"
                    max="2099"
                  />
                </div>
              </div>

              <div className="flex-row">
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Payment Mode</label>
                  <select
                    value={single.paymentMode}
                    onChange={(e) =>
                      setSingle({ ...single, paymentMode: e.target.value })
                    }
                  >
                    {PAYMENT_MODES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Transaction / Cheque No (optional)</label>
                  <input
                    type="text"
                    value={single.transactionRef}
                    onChange={(e) =>
                      setSingle({ ...single, transactionRef: e.target.value })
                    }
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ marginTop: 12, marginBottom: 12 }}>Bulk Receipt Range</h3>
              <div className="flex-row">
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Start Month</label>
                  <select
                    value={bulk.startMonth}
                    onChange={(e) =>
                      setBulk({ ...bulk, startMonth: Number(e.target.value) })
                    }
                  >
                    {FY_MONTH_ORDER.map((i) => (
                      <option key={i} value={i}>
                        {MONTHS[i]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Start Year</label>
                  <input
                    type="number"
                    value={bulk.startYear}
                    onChange={(e) =>
                      setBulk({ ...bulk, startYear: Number(e.target.value) })
                    }
                    min="2000"
                    max="2099"
                  />
                </div>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Number of Months</label>
                  <input
                    type="number"
                    value={bulk.months}
                    onChange={(e) =>
                      setBulk({ ...bulk, months: Number(e.target.value) })
                    }
                    min="1"
                    max="60"
                  />
                </div>
              </div>

              <div className="flex-row">
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Monthly Rent (₹)</label>
                  <div className="input-with-icon">
                    <FaRupeeSign className="icon" />
                    <input
                      type="number"
                      value={bulk.rentAmount === '' ? '' : bulk.rentAmount}
                      onChange={(e) =>
                        setBulk({
                          ...bulk,
                          rentAmount:
                            e.target.value === '' ? '' : Number(e.target.value),
                        })
                      }
                      min="0"
                      step="1"
                    />
                  </div>
                </div>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Payment Mode</label>
                  <select
                    value={bulk.paymentMode}
                    onChange={(e) =>
                      setBulk({ ...bulk, paymentMode: e.target.value })
                    }
                  >
                    {PAYMENT_MODES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          <div
            style={{
              marginTop: 14,
              padding: '10px 12px',
              borderRadius: 10,
              background: panRequired ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${panRequired ? '#fecaca' : '#bbf7d0'}`,
              fontSize: 13,
            }}
          >
            <div>
              <strong>Annual Rent:</strong> ₹ {formatINR(annualRent)}
            </div>
            <div style={{ marginTop: 4 }}>
              {panRequired
                ? 'Annual rent exceeds ₹1,00,000 — landlord PAN is mandatory for HRA exemption.'
                : 'Annual rent is within ₹1,00,000 — landlord PAN is not mandatory.'}
            </div>
          </div>

          <div className="button-group" style={{ marginTop: 16 }}>
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: 8 }} /> Reset
            </button>
            <button className="btn btn-primary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: 8 }} />
              {mode === 'bulk' ? `Download ${bulk.months} Receipts (PDF)` : 'Download PDF'}
            </button>
            <button className="btn btn-primary" onClick={handlePrint}>
              <FaPrint style={{ marginRight: 8 }} /> Print
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <h3 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaReceipt /> Preview {mode === 'bulk' && previewReceipts.length > 1 && (
              <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>
                (showing first of {previewReceipts.length})
              </span>
            )}
          </h3>

          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 0,
              maxWidth: 520,
              margin: '0 auto',
              boxShadow: '0 2px 12px rgba(15, 23, 42, 0.08)',
              fontFamily: 'Helvetica, Arial, sans-serif',
              color: '#0f172a',
              overflow: 'hidden',
            }}
          >
            <ReceiptPreview
              template={template}
              r={previewReceipts[0]}
              tenant={tenant}
              landlord={landlord}
              propertyAddress={propertyAddress}
              panRequired={panRequired}
              logo={logo}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentReceiptGenerator;
